/**
 * `/transfer-out/:token`: the Approve / Reject links the portal e-mails a domain's registrant when another registrar
 * asks to take the domain away (PHP-3941). No sign-in: the link's single-use token is the credential.
 *
 * It acts only on an explicit button press, never on load, so a mail scanner that opens links can't approve or reject
 * a transfer for the registrant. `?answer=approve|reject` (which link was clicked) only puts that button first.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router";

import { AuthLayout } from "@/components/layout";
import { Alert, Button, ErrorAlert, FullPageSpinner } from "@/components/ui";
import { domainsApi, longDate } from "@/lib/domains";
import { ApiError } from "@/lib/http";

const isDeadLink = (err: unknown) => err instanceof ApiError && err.reason === "TRANSFER_LINK_INVALID";

export function TransferOutAnswerPage() {
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const rejectFirst = params.get("answer") !== "approve";
  const request = useQuery({
    queryKey: ["transfer-out-link", token],
    queryFn: () => domainsApi.transferOutLink(token),
    enabled: Boolean(token),
    retry: false,
  });
  const answer = useMutation({
    mutationFn: (approve: boolean) => domainsApi.answerTransferOutLink(token, approve),
  });

  if (request.isPending && token) return <FullPageSpinner />;
  const r = request.data;

  if (answer.isSuccess) {
    const approved = answer.variables;
    return (
      <AuthLayout title={approved ? "Transfer approved" : "Transfer rejected"}>
        <div className="space-y-4 text-center">
          <CheckCircle2 className="mx-auto size-10 text-brand-600" aria-hidden />
          <p className="text-sm text-fg-muted">
            {approved
              ? `${answer.data.fqdn} is being released to the new registrar. It can take a few minutes to finish.`
              : `${answer.data.fqdn} stays with CortexGrip. The account owner has been told.`}
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={r ? `Transfer of ${r.fqdn}` : "Transfer request"}>
      {!r ? (
        <Alert tone="danger">
          {isDeadLink(request.error) || !token
            ? "This link has expired or was already used. "
            : "We couldn't load this transfer request. "}
          <Link to="/login" className="font-medium underline">
            Sign in
          </Link>{" "}
          to see the domain&apos;s transfers.
        </Alert>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-fg-muted">
            Another registrar asked on {longDate(r.detected_at)} to transfer{" "}
            <strong className="text-fg">{r.fqdn}</strong> away from CortexGrip. If nobody answers, it goes
            through automatically on <strong className="text-fg">{longDate(r.auto_completes_at)}</strong>.
          </p>
          <p className="text-sm text-fg-muted">
            Approve it only if you asked for this transfer. If you didn&apos;t, reject it to keep the domain
            here.
          </p>
          {isDeadLink(answer.error) ? (
            <Alert tone="danger">This link has expired or was already used.</Alert>
          ) : (
            <ErrorAlert error={answer.error} />
          )}
          <div className={rejectFirst ? "flex flex-col gap-2.5" : "flex flex-col-reverse gap-2.5"}>
            <Button
              className="w-full"
              variant={rejectFirst ? "primary" : "secondary"}
              loading={answer.isPending && answer.variables === false}
              disabled={answer.isPending}
              onClick={() => answer.mutate(false)}
            >
              Reject the transfer
            </Button>
            <Button
              className="w-full"
              variant={rejectFirst ? "secondary" : "primary"}
              loading={answer.isPending && answer.variables === true}
              disabled={answer.isPending}
              onClick={() => answer.mutate(true)}
            >
              Approve the transfer
            </Button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
